import { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, Card, CardContent,
    CardMedia, IconButton, Tooltip, Chip, Pagination,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DoneIcon from '@mui/icons-material/Done';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


/**
 * Lietotāju ziņojumi par citu lietotāju saturu (App Store 1.2 UGC prasība).
 *
 * Server endpoint'i (AdminModerationController.cs):
 *   POST /api/v1/admin/moderation/reports/pending
 *   POST /api/v1/admin/moderation/reports/decide   { reportId, action: 'dismiss' | 'hide' }
 *
 * "Noraidīt ziņojumu" - saturs paliek; "Paslēpt saturu" - atsauksmei Accepted=false,
 * foto soft-delete; visi pārējie ziņojumi par to pašu saturu aizveras automātiski.
 * Autoram push NEtiek sūtīts (klusa dzēšana, kā ārējo foto delete).
 */
type ContentType = 'REVIEW' | 'EXTERNAL_REVIEW' | 'REVIEW_PHOTO' | 'EXTERNAL_REVIEW_PHOTO';

type PendingReportItem = {
    reportId: string;
    reporterUserId: string;
    contentType: ContentType;
    authorUserId: string;
    placeId: string | null;
    source: string | null;
    externalId: string | null;
    imageId: string | null;
    reason: string;
    comment: string | null;
    contentText: string | null;
    contentImageUrl: string | null;
    placeAddress: string | null;
    contentAlreadyHidden: boolean;
    createdAt: string;
};

type PendingResponse = {
    isSuccess: boolean;
    error: string | null;
    totalPending: number;
    items: PendingReportItem[];
};


const PAGE_SIZE = 20;

const CONTENT_TYPE_LABEL: Record<ContentType, string> = {
    REVIEW: 'Atsauksme',
    EXTERNAL_REVIEW: 'Ārējā atsauksme',
    REVIEW_PHOTO: 'Atsauksmes foto',
    EXTERNAL_REVIEW_PHOTO: 'Ārējās atsauksmes foto',
};

const REASON_LABEL: Record<string, string> = {
    spam: 'Spams',
    offensive: 'Aizskarošs',
    inappropriate: 'Nepiemērots',
    other: 'Cits',
};


export function ReportsModerationPage() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    const skip = (page - 1) * PAGE_SIZE;

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['admin-report-moderation', skip],
        queryFn: async () => {
            const resp = await api.post<PendingResponse>(
                '/api/v1/admin/moderation/reports/pending',
                { skip, take: PAGE_SIZE },
            );
            return resp.data;
        },
        staleTime: 30_000,
    });

    const decideMutation = useMutation({
        mutationFn: async (args: { reportId: string; action: 'dismiss' | 'hide' }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/moderation/reports/decide',
                args,
            );
            if (!resp.data.isSuccess) {
                throw new Error(resp.data.error || 'Lēmums neizdevās.');
            }
            return args;
        },
        onMutate: (args) => setBusyKey(args.reportId),
        onSettled: () => setBusyKey(null),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-report-moderation'] });
        },
    });

    const items = data?.items ?? [];
    const totalPending = data?.totalPending ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalPending / PAGE_SIZE));

    useEffect(() => {
        if (!isLoading && items.length === 0 && page > 1) {
            setPage((p) => Math.max(1, p - 1));
        }
    }, [items.length, isLoading, page]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Satura ziņojumi</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Lietotāju ziņojumi par citu atsauksmēm un foto. "Paslēpt saturu" noņem saturu no
                        publiskā skata un aizver visus ziņojumus par to; "Noraidīt" atstāj saturu kā ir.
                        Autoram paziņojums netiek sūtīts.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                        label={`Kopā gaida: ${totalPending}`}
                        color={totalPending > 0 ? 'warning' : 'default'}
                    />
                    <Tooltip title="Atjaunot">
                        <span>
                            <IconButton onClick={() => refetch()} disabled={isFetching}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt ziņojumu rindu: {String((error as Error).message)}
                </Alert>
            )}

            {decideMutation.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {String((decideMutation.error as Error).message)}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && items.length === 0 && (
                <Alert severity="success">
                    Šobrīd nav neapstrādātu ziņojumu.
                </Alert>
            )}

            <Stack spacing={1.5}>
                {items.map((it) => (
                    <Card key={it.reportId}>
                        <CardContent>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                                {it.contentImageUrl && it.imageId && (
                                    <ReportedImageThumb url={it.contentImageUrl} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                                        <Chip size="small" label={CONTENT_TYPE_LABEL[it.contentType] ?? it.contentType} />
                                        <Chip size="small" color="warning" variant="outlined"
                                              label={REASON_LABEL[it.reason] ?? it.reason} />
                                        {it.contentAlreadyHidden && (
                                            <Chip size="small" color="default" label="Saturs jau paslēpts" />
                                        )}
                                        {it.placeAddress && (
                                            <Typography variant="caption" color="text.secondary">
                                                par {it.placeAddress}
                                            </Typography>
                                        )}
                                        {!it.placeAddress && it.source && (
                                            <Typography variant="caption" color="text.secondary">
                                                {it.source} / {it.externalId}
                                            </Typography>
                                        )}
                                    </Stack>

                                    {it.contentText && (
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                            {it.contentText}
                                        </Typography>
                                    )}
                                    {!it.contentText && !it.contentImageUrl && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            (saturs vairs nav pieejams)
                                        </Typography>
                                    )}

                                    {it.comment && (
                                        <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>
                                            Ziņotāja komentārs: {it.comment}
                                        </Typography>
                                    )}

                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(it.createdAt).toLocaleString('lv-LV')}
                                        </Typography>
                                        <Tooltip title={it.authorUserId}>
                                            <Typography variant="caption" color="text.secondary"
                                                        sx={{ fontFamily: 'monospace' }} noWrap>
                                                Autors: {it.authorUserId.slice(0, 8)}…
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={it.reporterUserId}>
                                            <Typography variant="caption" color="text.secondary"
                                                        sx={{ fontFamily: 'monospace' }} noWrap>
                                                Ziņotājs: {it.reporterUserId.slice(0, 8)}…
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        color="error"
                                        variant="contained"
                                        startIcon={<VisibilityOffIcon />}
                                        disabled={busyKey === it.reportId || it.contentAlreadyHidden}
                                        onClick={() => decideMutation.mutate({ reportId: it.reportId, action: 'hide' })}
                                    >
                                        Paslēpt saturu
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<DoneIcon />}
                                        disabled={busyKey === it.reportId}
                                        onClick={() => decideMutation.mutate({ reportId: it.reportId, action: 'dismiss' })}
                                    >
                                        Noraidīt
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        page={page}
                        count={pageCount}
                        onChange={(_, p) => setPage(p)}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Box>
    );
}


/**
 * Foto miniatūra. Image endpointi prasa X-Client-Token, ko <img src=> nepiesien -
 * lādējam caur axios kā blob (sk. PhotosPage.ModerationImageThumb).
 */
function ReportedImageThumb({ url }: { url: string }) {
    const { data: objectUrl, isLoading } = useQuery({
        queryKey: ['admin-report-photo-blob', url],
        queryFn: async () => {
            const resp = await api.get(url, { responseType: 'blob' });
            return URL.createObjectURL(resp.data as Blob);
        },
        staleTime: Infinity,
        gcTime: 5 * 60_000,
    });

    useEffect(() => {
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [objectUrl]);

    return (
        <Box sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0, bgcolor: '#0001' }}>
            {isLoading ? (
                <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <CardMedia
                    component="img"
                    image={objectUrl}
                    alt="Reported photo"
                    sx={{ height: 160, objectFit: 'cover' }}
                />
            )}
        </Box>
    );
}
